import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ServiciosPage = () => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    document.title = 'Servicios de Pintura Barcelona | PINTORDECO | Pintor Profesional';
    
    // Update canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${siteUrl}/servicios`);
    }
    
    // Update Open Graph meta tags
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `${siteUrl}/servicios`);
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Servicios de Pintura Barcelona | PINTORDECO | Pintor Profesional');
    }
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Servicios de pintura en Barcelona: interior, exterior, locales, oficinas, alisado de gotelé, lacados. Presupuesto gratis. ☎️ 690 22 09 44');
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Servicios de pintura en Barcelona: interior, exterior, locales, oficinas, alisado de gotelé, lacados. Presupuesto gratis.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'pintura interior Barcelona, alisar gotelé Barcelona, pintar local Barcelona, pintura exterior Barcelona, lacar puertas Barcelona');
    }

    // Structured Data - Services
    const scriptLD = document.createElement('script');
    scriptLD.type = 'application/ld+json';
    scriptLD.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${siteUrl}/servicios#service`,
      "serviceType": "Servicios de Pintura Profesional",
      "provider": {
        "@type": "ProfessionalService",
        "@id": `${siteUrl}/#organization`,
        "name": "PINTORDECO Barcelona",
        "url": siteUrl
      },
      "areaServed": {
        "@type": "City",
        "name": "Barcelona"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Servicios de Pintura",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Pintura Interior",
              "description": "Pintura de pisos, casas y viviendas en Barcelona"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Alisado de Gotelé",
              "description": "Eliminación de gotelé y alisado de paredes y techos"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Pintura de Locales Comerciales",
              "description": "Pintura profesional para tiendas, oficinas y espacios comerciales"
            }
          }
        ]
      }
    });
    document.head.appendChild(scriptLD);
    
    return () => {
      if (scriptLD.parentNode) {
        scriptLD.parentNode.removeChild(scriptLD);
      }
    };
  }, []);

  const serviciosPrincipales = [
    {
      titulo: 'Pintura Interior de Pisos',
      descripcion: 'Renovamos completamente tu piso: paredes, techos, puertas y radiadores. Preparación de superficies, protección de muebles y suelos, aplicación de pintura de calidad y limpieza final incluida.',
      imagen: 'https://public.readdy.ai/ai/img_res/647331b33b94d09d800516414405fd72.jpg',
      incluye: [
        'Valoración y medición gratuita',
        'Protección de suelos y muebles',
        'Reparación de grietas y desperfectos',
        'Lijado y masillado de superficies',
        'Aplicación de imprimación y pintura',
        'Limpieza y retirada de protecciones',
      ],
    },
    {
      titulo: 'Alisado de Gotelé',
      descripcion: 'Eliminamos el gotelé de paredes y techos dejando superficies completamente lisas y preparadas para pintar. Utilizamos técnicas profesionales que garantizan acabados perfectos sin marcas ni irregularidades.',
      imagen: 'https://readdy.ai/api/search-image?query=smooth%20ceiling%20wall%20texture%20removal%20finish%20modern%20apartment%20white%20clean%20surface&width=600&height=400&seq=servicio-gotele-2&orientation=landscape',
      incluye: [
        'Evaluación del estado del gotelé',
        'Protección total del espacio',
        'Alisado profesional con herramientas especializadas',
        'Lijado fino para acabado perfecto',
        'Imprimación de superficies',
        'Pintura final opcional',
      ],
    },
    {
      titulo: 'Pintura de Locales Comerciales',
      descripcion: 'Pintamos tiendas, oficinas, restaurantes y todo tipo de locales comerciales. Nos adaptamos a tus horarios para no interferir con tu actividad. Acabados profesionales que mejoran la imagen de tu negocio.',
      imagen: 'https://readdy.ai/api/search-image?query=modern%20commercial%20retail%20store%20interior%20white%20walls%20clean%20professional%20space%20bright%20lighting&width=600&height=400&seq=servicio-locales-2&orientation=landscape',
      incluye: [
        'Coordinación de horarios según tu actividad',
        'Pintura rápida y eficiente',
        'Pinturas especiales para zonas de tránsito',
        'Acabados mate, satinado o brillo',
        'Protección de escaparates y mobiliario',
        'Trabajo nocturno o fines de semana disponible',
      ],
    },
  ];

  const serviciosAdicionales = [
    {
      titulo: 'Pintura Exterior y Fachadas',
      icono: 'ri-building-line',
      imagen: 'https://readdy.ai/api/search-image?query=building%20exterior%20facade%20painting%20barcelona%20apartment%20block%20clean%20white%20walls&width=500&height=350&seq=servicio-fachadas-1&orientation=landscape',
    },
    {
      titulo: 'Esmaltes y Lacados',
      icono: 'ri-door-line',
      imagen: 'https://readdy.ai/api/search-image?query=painted%20lacquered%20white%20door%20enamel%20finish%20smooth%20glossy%20surface%20modern%20interior&width=500&height=350&seq=servicio-lacados-1&orientation=landscape',
    },
    {
      titulo: 'Pintura de Oficinas',
      icono: 'ri-briefcase-line',
      imagen: 'https://readdy.ai/api/search-image?query=modern%20office%20space%20interior%20white%20walls%20clean%20professional%20workspace%20bright%20lighting&width=500&height=350&seq=servicio-oficinas-1&orientation=landscape',
    },
    {
      titulo: 'Pintura de Techos',
      icono: 'ri-layout-top-line',
      imagen: 'https://readdy.ai/api/search-image?query=white%20painted%20ceiling%20smooth%20finish%20modern%20interior%20room%20clean%20professional%20result&width=500&height=350&seq=servicio-techos-1&orientation=landscape',
    },
    {
      titulo: 'Reparación de Grietas',
      icono: 'ri-tools-line',
      imagen: 'https://readdy.ai/api/search-image?query=wall%20crack%20repair%20plastering%20fixing%20damaged%20surface%20professional%20restoration%20work&width=500&height=350&seq=servicio-grietas-1&orientation=landscape',
    },
    {
      titulo: 'Pintura de Comunidades',
      icono: 'ri-community-line',
      imagen: 'https://readdy.ai/api/search-image?query=apartment%20building%20stairwell%20hallway%20painted%20white%20walls%20clean%20residential%20common%20area&width=500&height=350&seq=servicio-comunidades-1&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 min-h-[400px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=painting%20tools%20equipment%20brushes%20rollers%20paint%20cans%20professional%20supplies%20organized%20workspace&width=1920&height=600&seq=servicios-hero-bg-1&orientation=landscape)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <nav className="text-sm text-white/80 mb-6">
            <Link to="/" className="hover:text-accent cursor-pointer">Inicio</Link>
            <span className="mx-2">/</span>
            <span className="text-white font-semibold">Servicios</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Servicios de pintura en Barcelona
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Soluciones completas de pintura profesional para pisos, casas, locales y oficinas. Más de 15 años de experiencia transformando espacios en Barcelona. <Link to="/empresa" className="text-white hover:text-accent transition-colors underline">Conoce nuestro equipo profesional</Link>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Servicios Principales */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-4">
              Nuestros servicios principales
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ofrecemos servicios completos de pintura profesional con garantía de calidad en cada proyecto. <Link to="/faq" className="text-gray-600 hover:text-accent transition-colors underline">Consulta preguntas frecuentes</Link> sobre nuestros servicios.
            </p>
          </motion.div>

          <div className="space-y-12">
            {serviciosPrincipales.map((servicio, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow"
              >
                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Imagen */}
                  <div className="relative h-80 lg:h-auto">
                    <img
                      src={servicio.imagen}
                      alt={servicio.titulo}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent lg:hidden"></div>
                  </div>

                  {/* Contenido */}
                  <div className="p-8 lg:p-12">
                    <h3 className="text-3xl font-bold text-primary mb-4">
                      {servicio.titulo}
                    </h3>
                    <p className="text-gray-700 mb-6 leading-relaxed">
                      {servicio.descripcion}
                    </p>

                    <h4 className="text-lg font-bold text-primary mb-4">¿Qué incluye este servicio?</h4>
                    <ul className="space-y-3 mb-6">
                      {servicio.incluye.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/contacto"
                      className="whitespace-nowrap inline-flex items-center space-x-2 px-6 py-3 bg-accent text-white rounded-full font-semibold hover:bg-accent/90 transition-all cursor-pointer"
                    >
                      <span>Solicitar presupuesto</span>
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Servicios Adicionales */}
      <section className="py-20 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-4">
              Otros servicios de pintura
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              También ofrecemos otros servicios especializados de pintura para cubrir todas tus necesidades. <Link to="/contacto" className="text-gray-600 hover:text-accent transition-colors underline">Solicita información personalizada</Link>.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviciosAdicionales.map((servicio, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative rounded-2xl overflow-hidden group cursor-pointer shadow-lg h-72"
              >
                <img
                  src={servicio.imagen}
                  alt={servicio.titulo}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center mb-3">
                    <i className={`${servicio.icono} text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold">{servicio.titulo}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Información Importante */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Qué necesitamos para presupuestar */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-primary/5 rounded-3xl p-8"
            >
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-questionnaire-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-primary mb-4">
                ¿Qué necesitamos para presupuestar?
              </h3>
              <p className="text-gray-700 mb-6">
                Para ofrecerte un presupuesto preciso y ajustado, necesitamos conocer algunos detalles de tu proyecto. <Link to="/faq" className="text-gray-700 hover:text-accent transition-colors underline">Ver preguntas frecuentes sobre presupuestos</Link>:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <i className="ri-camera-line text-accent text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700"><strong>Fotos o vídeo</strong> del espacio a pintar (paredes, techos, estado actual)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-ruler-line text-accent text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700"><strong>Metros cuadrados</strong> aproximados (si los conoces)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-home-line text-accent text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700"><strong>Estado actual:</strong> ¿necesita reparaciones? ¿Tiene gotelé?</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-sofa-line text-accent text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700"><strong>Muebles:</strong> ¿El piso está vacío o amueblado?</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-time-line text-accent text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700"><strong>Plazos y horarios</strong> preferidos para el trabajo</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-palette-line text-accent text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700"><strong>Colores y acabados</strong> deseados (mate, satinado, brillo)</span>
                </li>
              </ul>
            </motion.div>

            {/* Tiempos Orientativos */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-accent/5 rounded-3xl p-8"
            >
              <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-timer-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-primary mb-4">
                Tiempos orientativos
              </h3>
              <p className="text-gray-700 mb-6">
                Los plazos varían según el estado de las superficies, metros cuadrados y servicios contratados. <Link to="/empresa" className="text-gray-700 hover:text-accent transition-colors underline">Conoce cómo trabajamos</Link>:
              </p>
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <strong className="text-primary">Piso 60-80m²</strong>
                    <span className="text-accent font-bold">3-5 días</span>
                  </div>
                  <p className="text-sm text-gray-600">Pintura completa, preparación incluida</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <strong className="text-primary">Alisado de gotelé 60m²</strong>
                    <span className="text-accent font-bold">4-6 días</span>
                  </div>
                  <p className="text-sm text-gray-600">Incluye alisado, lijado e imprimación</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <strong className="text-primary">Local comercial 100m²</strong>
                    <span className="text-accent font-bold">2-4 días</span>
                  </div>
                  <p className="text-sm text-gray-600">Según horarios y disponibilidad</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <strong className="text-primary">Habitación individual</strong>
                    <span className="text-accent font-bold">1-2 días</span>
                  </div>
                  <p className="text-sm text-gray-600">Paredes y techo, preparación básica</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-6 italic">
                * Tiempos aproximados. Cada proyecto se valora individualmente según sus características.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tabla Comparativa */}
      <section className="py-20 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-4">
              ¿Qué incluye cada servicio?
            </h2>
            <p className="text-xl text-gray-600">
              Comparativa de qué incluye cada <Link to="/" className="text-gray-600 hover:text-accent transition-colors underline">servicio de pintura profesional</Link>
            </p>
          </motion.div>

          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-primary text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Servicio incluido</th>
                    <th className="px-6 py-4 text-center font-semibold">Pintura Interior</th>
                    <th className="px-6 py-4 text-center font-semibold">Alisado Gotelé</th>
                    <th className="px-6 py-4 text-center font-semibold">Locales</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { servicio: 'Valoración gratuita', interior: true, gotele: true, locales: true },
                    { servicio: 'Protección de suelos y muebles', interior: true, gotele: true, locales: true },
                    { servicio: 'Reparación de grietas', interior: true, gotele: false, locales: false },
                    { servicio: 'Lijado y masillado', interior: true, gotele: true, locales: true },
                    { servicio: 'Alisado de superficies', interior: false, gotele: true, locales: false },
                    { servicio: 'Imprimación', interior: true, gotele: true, locales: true },
                    { servicio: 'Pintura de paredes', interior: true, gotele: true, locales: true },
                    { servicio: 'Pintura de techos', interior: true, gotele: true, locales: false },
                    { servicio: 'Coordinación de horarios', interior: false, gotele: false, locales: true },
                    { servicio: 'Limpieza final', interior: true, gotele: true, locales: true },
                  ].map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 font-medium text-gray-900">{row.servicio}</td>
                      <td className="px-6 py-4 text-center">
                        {row.interior ? (
                          <i className="ri-checkbox-circle-fill text-2xl text-accent"></i>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row.gotele ? (
                          <i className="ri-checkbox-circle-fill text-2xl text-accent"></i>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row.locales ? (
                          <i className="ri-checkbox-circle-fill text-2xl text-accent"></i>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Servicio Personalizado */}
      <section className="py-12 bg-primary">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="text-white">
              <h3 className="text-2xl font-bold mb-2">¿No encuentras lo que buscas?</h3>
              <p className="text-white/90">Consulta por servicios personalizados según tus necesidades. <Link to="/faq" className="text-white hover:text-accent transition-colors underline">Revisa las preguntas frecuentes</Link>.</p>
            </div>
            <Link
              to="/contacto"
              className="whitespace-nowrap px-8 py-4 bg-accent text-white rounded-full font-semibold text-lg hover:bg-accent/90 transition-all cursor-pointer shadow-lg flex-shrink-0"
            >
              Consultar servicio personalizado
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              ¿Necesitas asesoramiento profesional?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Solicita tu <Link to="/contacto" className="text-gray-600 hover:text-accent transition-colors font-semibold underline">presupuesto gratuito</Link> y sin compromiso. Te respondemos en menos de 24 horas con un presupuesto detallado.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+34690220944"
                className="whitespace-nowrap inline-flex items-center justify-center space-x-3 px-8 py-4 bg-primary text-white rounded-full font-semibold text-lg hover:bg-primary/90 transition-all cursor-pointer shadow-lg"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>Llamar: 690 22 09 44</span>
              </a>
              <a
                href="https://wa.me/34690220944"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center justify-center space-x-3 px-8 py-4 bg-accent text-white rounded-full font-semibold text-lg hover:bg-accent/90 transition-all cursor-pointer shadow-lg"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                <span>WhatsApp</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiciosPage;
